/**
 * Every read the app makes, one hook each, one key each.
 *
 * Collected here for the same reason `db.ts` collects the writes: the shape of
 * what the app asks the database for should be readable in one file. The keys
 * are the contract — a screen names a key, the store answers, and two screens
 * naming the same key share one request and one copy of the rows.
 *
 * Rule 4 of the brief lives here: **one `fetchAll(feed)` in the whole app**,
 * and it is `fetchFeed` below. Five screens used to run their own, which on a
 * tab switch meant re-pulling 2,400 rows to look at the same thing again.
 */
import { useCallback, useMemo } from 'react'
import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { useAuth } from './auth'
import { supabase } from './supabase'
import {
  COLD, decodeFeedPage, decodeShowingPage, feedIsPartial, feedPages, fetchAll, mergeFeedRows,
  type FeedPagePayload, type FeedPageRange, type ShowingPagePayload,
} from './fetchAll'
import { affinityKey, type AffinityMap, type AffinityRow } from './affinity'
import {
  mergeScores, scoreCursor, scoreMapOf, withScores, type ScoreCache, type ScoreMap, type ScoreWireRow,
} from './userScores'
import { mergeProfile, type Profile } from './profileDefaults'
import { useCurrentRegion, useRegionKnown, useRegionPlace, placePoint } from './currentRegion'
import { queryClient } from './store'
import { addDays, iso } from './calendar'
import { attentionItems, type AttentionItem } from './attention'
import { deriveFeed, indexShowings, inheritVenueHeroes, type VenueRow } from './derive'
import { parseForecast, type DayForecast } from './weather'
import { fetchVenueSourcing } from './venueSourcing'
import { fetchVenueNotes } from './venueNotes'
import { fetchOrganisation, fetchUpcomingEvents, fetchUpcomingEventCounts } from './venueRecord'
import { listUsuals } from './db'
import type { ShowingLite } from '../components/CardBody'
import type {
  Alert, AppNote, Commitment, FeedDetail, FeedLiteRow, Flight, Lead, LeaveDay, LeaveYear, List, ListItem,
  ListRun, ListUsual, NewsRow, Recipe, ReleaseNote, SeriesRow, Stay, Summary, Visitor,
} from './types'
import type { District, Region, RegionTown, RegionsWire } from './regions'
import type { Database } from './database.types'

export type { VenueRow } from './derive'

/**
 * A Supabase result, as a promise that rejects.
 *
 * `postgrest-js` returns its error rather than throwing it, and TanStack Query
 * decides "did this fail" from a rejection. Everything that used to read
 * `r.error` and hand it to `useQuery`'s own error slot throws here instead,
 * which is also what puts a JWT rejection in front of the retry predicate in
 * `store.ts`.
 */
async function rows<T>(
  q: PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
): Promise<T[]> {
  const { data, error } = await q
  if (error) throw new Error(error.message)
  return data ?? []
}

/** `rows`, for a table that can pass PostgREST's silent 1,000-row cap: every
 *  page, through `fetchAll`. The builder must be ordered on something unique,
 *  and is a thunk because a builder is spent once awaited. */
async function allRows<T>(query: Parameters<typeof fetchAll>[0]): Promise<T[]> {
  const { data, error } = await fetchAll<T>(query)
  if (error) throw new Error(error.message)
  return data
}

/**
 * A query that waits for a session.
 *
 * Every table here is behind RLS on `auth.uid()`, so a request made before
 * `getSession()` has resolved is at best empty and at worst a rejected token —
 * and with all five tabs mounted that is eighteen of them at once on the sign-
 * in screen. The old code said the same thing in one place, in the badge
 * query's `if (!session) return { data: 0 }`; it says it here now because the
 * badge is no longer the only reader.
 *
 * This is not a substitute for `store.ts`'s transient retry and does not make
 * it redundant. A token can be rejected as `issued at future` long after
 * sign-in, on any refresh, and a session that exists is not the same as a
 * token PostgREST will currently accept.
 *
 * A cached answer still renders while this is false — `enabled: false` stops
 * a fetch, not a read — so a cold start with a persisted cache paints before
 * the session resolves, which is what WP 1.1 moved the shell in front of.
 */
export function useAuthed<TQueryFnData, TData = TQueryFnData>(
  options: UseQueryOptions<TQueryFnData, Error, TData>,
) {
  const { session } = useAuth()
  return useQuery({ ...options, enabled: !!session })
}

// ---------------------------------------------------------------------------
// The feed
// ---------------------------------------------------------------------------

/** X13: the client holds this many weeks, the server holds the rest. One
 *  constant, read by `fetchFeed` and nowhere else. */
export const HORIZON_WEEKS = 12

/** What `['feed', regionId]` actually caches — `feed_page`'s own cursor alongside the
 *  rows, so the next fetch knows where to resume from without re-deriving it
 *  from the rows themselves (rule: `updated_at` is never compared
 *  client-side; `as_of` from the server is the only cursor). */
interface FeedCache {
  as_of: string
  rows: FeedLiteRow[]
}

async function fetchFeedPage(since: string, range: FeedPageRange): Promise<FeedPagePayload> {
  const { data, error } = await supabase.rpc('feed_page', { since, ...range })
  if (error) throw new Error(error.message)
  return data as unknown as FeedPagePayload
}

/**
 * `feed_lite` with a horizon and a delta (X2, X13), replacing the old
 * `fetchAll(feed)`. A warm cache asks `feed_page` for everything since its
 * own `as_of`, in one call, and merges the answer onto what it already has —
 * upsert the changed rows, drop the ones `removed` names, keep everything
 * else untouched.
 *
 * A cold one (no cache, or a cache still at `COLD`) asks in two pages, the
 * next `COLD_SPLIT_WEEKS` and then the rest (fix-cold-load: one 7 MB pull
 * took 2 s alone and timed out whenever two overlapped). The first page goes
 * into the cache as soon as it lands, so Today paints from it (`useFeed`
 * says who else may), but **at `COLD`,
 * not at its own `as_of`**: a cursor says "complete from here", and a cache
 * missing weeks 2–12 is complete from nowhere. Were it stamped with a real
 * time and the second page then failed, every later pull would be a delta
 * from that time and the far weeks would never arrive. When both pages are
 * in, the cache takes the *first* page's `as_of` — the earlier of the two —
 * so anything that changed between the two calls is in the next delta.
 *
 * A request that fails throws, and TanStack keeps the query's last `data`:
 * the old cache on a warm pull, the first page (still at `COLD`) on a cold
 * one, which the next focus pulls cold again from the start.
 */
async function fetchFeed(regionId: string): Promise<FeedCache> {
  const key = feedKey(regionId)
  const prev = queryClient.getQueryData<FeedCache>(key)
  const cold = !prev || prev.as_of === COLD

  if (!cold) {
    const page = await fetchFeedPage(prev.as_of, { from_week: 0, to_week: HORIZON_WEEKS, region: regionId })
    return { as_of: page.as_of, rows: mergeFeedRows(prev.rows, decodeFeedPage<FeedLiteRow>(page), page.removed) }
  }

  let as_of: string | null = null
  let rows: FeedLiteRow[] = []
  const pages = feedPages(true, HORIZON_WEEKS, undefined, regionId)
  for (let i = 0; i < pages.length; i++) {
    const page = await fetchFeedPage(COLD, pages[i])
    as_of ??= page.as_of
    rows = mergeFeedRows(rows, decodeFeedPage<FeedLiteRow>(page), [])
    // `updatedAt: 0`: a partial cache is never fresh. Persisted and restored
    // by a reopen mid-pull, it refetches on mount instead of sitting inside
    // `staleTime` for five minutes without its far weeks.
    if (i < pages.length - 1) queryClient.setQueryData<FeedCache>(key, { as_of: COLD, rows }, { updatedAt: 0 })
  }
  return { as_of: as_of ?? COLD, rows }
}

/** `['feed', regionId]`. `mutations.ts` invalidates the `['feed']` prefix, which
 *  reaches every region's cache. */
export const feedKey = (regionId: string) => ['feed', regionId] as const

/** One function, not an arrow per call: TanStack re-runs `select` whenever
 *  its identity changes, which an inline arrow does on every render. */
const selectFeed = (cache: FeedCache) => ({ rows: deriveFeed(cache.rows), partial: feedIsPartial(cache) })

/**
 * The feed, for a screen. `data`, `isPending` and `error` mean what they mean
 * on a query result, with one difference: **a partial cache — the first page
 * of a cold pull, while the second is in flight — is still pending** unless
 * the caller asks for the soonest rows (`{ soonest: true }`, Today alone).
 *
 * a1-17: the first page was always in the cache before the second arrived,
 * and every mounted screen drew from it — Browse's "All 829" and its lane
 * counts, "54 more concerts", a Saved list missing everything past two
 * weeks — as though it were the whole horizon. Today reads nothing past
 * `AHEAD_DAYS` in its upper sections, which is the first page exactly
 * (`COLD_SPLIT_WEEKS`); its sections that look further read `partial` and
 * wait. Holding the rest back also keeps the four hidden screens from
 * rendering 1,100 rows they will render again three seconds later, on the
 * main thread Today is trying to paint with.
 *
 * A partial cache whose second page *failed* is not pending: `error` is set
 * and the screen shows it, as it did before for any failed refetch — not a
 * skeleton that never ends.
 */
export function useFeed({ soonest = false }: { soonest?: boolean } = {}) {
  // C2.2: one cache per region, each with its own `as_of` — a delta cursor is
  // valid only under the filter it was taken with. The id comes from the
  // query's own key, not from a module global read at fetch time, so a fetch
  // that was started under one region can never write into another's cache.
  const { region } = useCurrentRegion()
  // Not before the answer to "which region" has been read: see
  // `useRegionKnown`. Everything `useAuthed` does, plus that.
  const { session } = useAuth()
  const known = useRegionKnown()
  const q = useQuery({
    queryKey: feedKey(region.id),
    queryFn: ({ queryKey }) => fetchFeed(queryKey[1] as string),
    select: selectFeed,
    enabled: !!session && known,
  })
  // WP U2.3: the score on a row is the signed-in person's own, laid on here —
  // once, for every screen — from `user_entry_score` (see `userScores.ts`).
  const scoresQ = useScores()
  const partial = q.data?.partial ?? false
  const hold = partial && !soonest
  const feedRows = q.data?.rows
  const scores = scoresQ.data
  const rows = useMemo(
    () => (feedRows ? withScores(feedRows, scores) : undefined),
    [feedRows, scores],
  )
  // The feed waits for the scores' first answer, so a cold start does not paint
  // every row at the null default and then reorder when they arrive. Only the
  // first answer: a read that fails (`isError`) draws the feed unscored rather
  // than not at all, and a cached map paints at once while it refreshes.
  const waitingOnScores = scoresQ.isPending && !scoresQ.isError
  return {
    data: hold ? undefined : rows,
    isPending: q.isPending || (hold && !q.error) || (waitingOnScores && !q.error),
    error: q.error,
    /** True while `data` is only the first page — only ever seen by a
     *  `soonest` caller, since anyone else gets no `data` until it is not. */
    partial,
  }
}

// ---------------------------------------------------------------------------
// The signed-in person's scores (WP U2.3)
// ---------------------------------------------------------------------------

/** `['user_entry_score', uid]` — the user in the key so a second account on
 *  the same tab can never read the first one's scores from memory (the
 *  persisted cache is per user already, U0.4). */
export const scoresKey = (uid: string | undefined) => ['user_entry_score', uid] as const

/**
 * Every score the person has on first read, then the rows scored since the
 * last one. `user_entry_score` is RLS-scoped to its owner and the filter on
 * `user_id` is for the index; a first read is about five thousand rows for a
 * person the model has scored for a month, six pages through `fetchAll`.
 * Ordered by `entry_id`, unique within the person, as `fetchAll` requires.
 */
async function fetchScores(uid: string): Promise<ScoreCache> {
  const prev = queryClient.getQueryData<ScoreCache>(scoresKey(uid))
  const since = scoreCursor(prev)
  const data = await allRows<ScoreWireRow>(() => {
    const q = supabase.from('user_entry_score').select('entry_id,score,scored_at').eq('user_id', uid)
    return (since ? q.gte('scored_at', since) : q).order('entry_id')
  })
  return mergeScores(prev, data)
}

/** Hoisted, as `affinityByKey` is: a stable `select`, so the `Map` is new only
 *  when the cache is. */
const selectScores = (cache: ScoreCache): ScoreMap => scoreMapOf(cache)

/**
 * The signed-in person's scores, by entry id. Persisted with the rest of the
 * cache and refetched with the feed (the same `staleTime` and focus refetch),
 * so a warm start paints Doug's order from what the last visit knew. Read it
 * through `useFeed`, which has laid it on the rows; a screen asks for the map
 * itself only to say something about the scores, not to sort.
 */
export function useScores() {
  const { session } = useAuth()
  const uid = session?.user.id
  return useQuery({
    queryKey: scoresKey(uid),
    queryFn: () => fetchScores(uid!),
    select: selectScores,
    enabled: !!uid,
  })
}

/** The sheet's columns that do not ride along on every row — see
 *  `FeedDetail`'s own note in `types.ts` for why. `staleTime` is generous
 *  because they change only when a harvest does, not between visits to the
 *  same sheet. */
export function useFeedDetail(id: string | null) {
  const { session } = useAuth()
  return useQuery<FeedDetail>({
    queryKey: ['feed_detail', id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('feed_detail', { entry_id: id as string })
      if (error) throw new Error(error.message)
      return (data ?? { score_reasons: null }) as unknown as FeedDetail
    },
    enabled: !!session && !!id,
    staleTime: 60 * 60_000,
  })
}

/** W3: the blurb for each row of a list. `feed_lite` carries no summary, and
 *  `feed_detail` answers for one row, so a list asks for the ids on screen in
 *  one call. The key is the sorted ids, so scrolling back to a set already
 *  asked costs nothing. A film's overview is not here: it is already on the
 *  row as `film.overview`, so a row's description is
 *  `summary ?? film.overview ?? null`. */
export function useSummaries(ids: string[]) {
  const { session } = useAuth()
  const sorted = [...new Set(ids)].sort()
  return useQuery<Record<string, Summary>>({
    queryKey: ['feed_summaries', sorted],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('feed_summaries', { ids: sorted })
      if (error) throw new Error(error.message)
      const out: Record<string, Summary> = {}
      for (const r of data ?? []) {
        if (r.summary) out[r.id] = { summary: r.summary, source_url: r.summary_source_url }
      }
      return out
    },
    enabled: !!session && sorted.length > 0,
    staleTime: 60 * 60_000,
  })
}

/** WP 9.4: what the butler already found out about an event —
 *  `entry_research`'s live rows (`superseded_at is null`), newest first.
 *  Read by the app's own session, not through the Worker — the one butler
 *  table the migration says is load-bearing for that from day one
 *  (`20260928120000_butler.sql`). A merged film card's several ids are
 *  asked in one request, so the sheet's Research section reads across the
 *  group the way `event_detail` already does. */
export interface EntryResearchRow {
  id: string
  entry_id: string
  kind: 'reviews' | 'background' | 'availability' | 'programme'
  body: string
  sources: { url: string; title: string }[]
  created_at: string
}

export function useEntryResearch(ids: string[]) {
  const { session } = useAuth()
  const key = useMemo(() => [...new Set(ids)].sort(), [ids.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps
  return useQuery<EntryResearchRow[]>({
    queryKey: ['entry_research', key],
    queryFn: () => rows<EntryResearchRow>(
      supabase.from('entry_research')
        .select('id,entry_id,kind,body,sources,created_at')
        .in('entry_id', key)
        .is('superseded_at', null)
        .order('created_at', { ascending: false }),
    ),
    enabled: !!session && key.length > 0,
    staleTime: 5 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// People and ensembles (Phase E5)
// ---------------------------------------------------------------------------

/** What a chip needs about a person when the row it came from does not carry
 *  it: `feed_lite.people` is `[{id, role}]` (K11), so a place's "Artists
 *  shown" and Browse's `?person=` header have ids and no names. The open
 *  sheet never uses this — `feed_detail` already hands it the full objects. */
export interface PersonRow {
  id: string
  name: string
  description: string | null
  born: number | null
  died: number | null
  wikipedia_url: string | null
  reconcile_status: string | null
}

export interface EnsembleRow { id: string; name: string }

/** PostgREST puts `in.(…)` in the query string, and a gallery with thirty
 *  artists across five shows is already a long URL; 40 ids a request keeps it
 *  inside every proxy's request-line limit with room to spare. */
const ID_CHUNK = 40

/** The chunking, once. The `select` stays a literal at each call site: handed
 *  a `string`, supabase-js's generated types fall back to `GenericStringError`
 *  and the row type is lost. */
async function inChunks<T>(
  ids: string[],
  get: (chunk: string[]) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
): Promise<T[]> {
  const out: T[] = []
  for (let i = 0; i < ids.length; i += ID_CHUNK) out.push(...await rows<T>(get(ids.slice(i, i + ID_CHUNK))))
  return out
}

/** The `people` rows behind a set of ids. Keyed on the sorted, de-duplicated
 *  list so two callers asking for the same artists share one request, the
 *  same shape `useEntryResearch` uses for a merged film card's ids.
 *
 *  `staleTime` is generous: E2's nightly slice is the only writer, and a
 *  person's description and years do not change between two looks at the same
 *  gallery. */
export function usePeople(ids: string[]) {
  const { session } = useAuth()
  const key = useMemo(() => [...new Set(ids)].sort(), [ids.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps
  return useQuery<PersonRow[]>({
    queryKey: ['people', key],
    queryFn: () => inChunks<PersonRow>(key, chunk => supabase.from('people')
      .select('id,name,description,born,died,wikipedia_url,reconcile_status')
      .in('id', chunk)),
    enabled: !!session && key.length > 0,
    staleTime: 60 * 60_000,
  })
}

/** The same, for ensembles — the name only, because E3 stores no facts.
 *  Browse's `?ensemble=` header needs it: a name derived from the slug would
 *  print "Il Pomo D Oro" for a row that knows it is "Il Pomo d'Oro". */
export function useEnsembles(ids: string[]) {
  const { session } = useAuth()
  const key = useMemo(() => [...new Set(ids)].sort(), [ids.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps
  return useQuery<EnsembleRow[]>({
    queryKey: ['ensembles', key],
    queryFn: () => inChunks<EnsembleRow>(key, chunk => supabase.from('ensembles')
      .select('id,name').in('id', chunk)),
    enabled: !!session && key.length > 0,
    staleTime: 60 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// Taste affinity (Phase E4, K5) — read time only
// ---------------------------------------------------------------------------

/** One small query per app load, cached like the rest (rule 7) — a failed
 *  load leaves `data` undefined and the caller falls back to an empty map,
 *  the signal's own best-effort rule (`db.ts`'s note on `logSignal`). */
async function fetchAffinity(): Promise<AffinityRow[]> {
  return rows<AffinityRow>(supabase.from('taste_affinity').select('kind,id,positive,negative'))
}

/** Hoisted for the same reason `seen.ts`'s own `seenByEntry` is: a stable
 *  `select` so the `Map` TanStack hands back is only ever new when the
 *  underlying rows actually changed, not on every render that reads it. */
const affinityByKey = (rowsIn: AffinityRow[]): AffinityMap =>
  new Map(rowsIn.map(r => [affinityKey(r.kind, r.id), r]))

/** Doug's own history with the people and ensembles on a row, summed by the
 *  `taste_affinity` view. Invalidated by every mutation that writes a taste
 *  signal (`useSaveEntry`, `useDismissEntry`, `useMarkWent`,
 *  `useScheduleEntry`, `seen.ts`'s `useMarkSeen`). */
export const useAffinity = () =>
  useAuthed({ queryKey: ['affinity'], queryFn: fetchAffinity, select: affinityByKey })

// ---------------------------------------------------------------------------
// The signed-in person's profile (U1.2)
// ---------------------------------------------------------------------------

/** `['profile', uid]` — the user in the key so a second account on the same
 *  tab can never read the first one's row from memory (the persisted cache is
 *  per user already, U0.4). One row, RLS-scoped; a trigger creates it at
 *  sign-up, so `null` only means a user older than the migration whose
 *  backfill has not run. */
export const profileKey = (uid: string | undefined) => ['profile', uid] as const

async function fetchProfile(uid: string): Promise<Profile | null> {
  const { data, error } = await supabase.from('profiles').select('*').eq('user_id', uid).maybeSingle()
  if (error) throw new Error(error.message)
  return data
}

/** The row laid over `DEFAULT_PROFILE` by `mergeProfile` (hoisted: a stable
 *  `select`, so the merged object is new only when the row is). An hour's
 *  `staleTime`: the profile changes when its owner edits it, and the edit
 *  patches this key itself. Read it through `useProfile()`, not here. */
export function useProfileRow() {
  const { session } = useAuth()
  const uid = session?.user.id
  return useQuery({
    queryKey: profileKey(uid),
    queryFn: () => fetchProfile(uid!),
    select: mergeProfile,
    enabled: !!uid,
    staleTime: 60 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// C2.2: the person's place in each region
// ---------------------------------------------------------------------------

type ProfilePlaceRow = Database['public']['Tables']['profile_places']['Row']

/** `['profile_places', uid]` — a pin, a label, districts and lines per region
 *  (C0.2). A handful of rows, RLS-scoped to their owner; the user in the key
 *  for the reason `profileKey` has it. Read it through `useRegionPlace()`,
 *  which applies C0.2's reader rule (this row, else the profile's columns for
 *  the home region, else nothing). */
export function useProfilePlaces() {
  const { session } = useAuth()
  const uid = session?.user.id
  return useQuery({
    queryKey: ['profile_places', uid],
    queryFn: () => rows<ProfilePlaceRow>(supabase.from('profile_places').select('*').eq('user_id', uid!)),
    enabled: !!uid,
    staleTime: 60 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// C2.1: regions
// ---------------------------------------------------------------------------

/** `regions`, `region_towns` and `districts` (C0.1), whole: two rows, about
 *  150 towns and a hundred-odd districts. Any one failing fails the lot, and
 *  `regions.ts`'s `useRegions()` reads the bundled copy instead — the tables
 *  do not exist until C0.1 is applied, and a missing relation is not one of
 *  the transient errors `store.ts` retries. */
async function fetchRegions(): Promise<RegionsWire> {
  const [regions, towns, districts] = await Promise.all([
    rows<Region>(supabase.from('regions')
      .select('id,name,country,timezone,locale,currency,holidays,lat,lng,zoom').order('id')),
    rows<RegionTown>(supabase.from('region_towns').select('region_id,town,tier').order('town')),
    rows<District>(supabase.from('districts').select('ortsteil,bezirk,region_id').order('ortsteil')),
  ])
  return { regions, towns, districts }
}

/** Read it through `regions.ts`'s `useRegions()`, which passes the `select`.
 *  Persisted with everything else; a day's `staleTime`, because a region
 *  changes when a migration says so. No `CACHE_BUSTER` bump: the key is new. */
export function useRegionsQuery<T>(select: (w: RegionsWire) => T) {
  return useAuthed<RegionsWire, T>({
    queryKey: ['regions'],
    queryFn: fetchRegions,
    select,
    staleTime: 24 * 60 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// U2.4: the active taste vector
// ---------------------------------------------------------------------------

/** What the app needs to know of `taste_profile`: whether the signed-in person
 *  has an active row (onboarding's second step asks if not), which version it
 *  is (Account offers the form only over a 2.0 or nothing), and the vector to
 *  fill the form with. `document`, the dossier, is not granted and not
 *  selected. `null` is a person with no active row. */
export type ActiveTaste = { version: string; vector: unknown } | null

export const activeTasteKey = (uid: string | undefined) => ['taste_active', uid] as const

async function fetchActiveTaste(uid: string): Promise<ActiveTaste> {
  const { data, error } = await supabase.from('taste_profile').select('version, vector')
    .eq('user_id', uid).eq('is_active', true).maybeSingle()
  if (error) throw new Error(error.message)
  return data
}

/** `undefined` data while pending or failed, which callers must not read as
 *  "has none" (`needsTaste` does not). Patched by the save itself, so no
 *  refetch is waiting on the write. */
export function useActiveTaste() {
  const { session } = useAuth()
  const uid = session?.user.id
  return useQuery({
    queryKey: activeTasteKey(uid),
    queryFn: () => fetchActiveTaste(uid!),
    enabled: !!uid,
    staleTime: 60 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// Series (nightlife N7)
// ---------------------------------------------------------------------------

/** Hoisted, as `affinityByKey` is: a stable `select` so the `Map` is new
 *  only when the rows are. */
const seriesById = (list: SeriesRow[]): Map<string, SeriesRow> => new Map(list.map(r => [r.id, r]))

/**
 * Every party, quiz and residency, by id — the names `feed_lite.series`
 * does not carry (it sends `[{id, role}]`, K11's thin shape, as `people`).
 * One small request per app load, shared by every club row, the sheet and
 * Browse's `?series=` page under one key, and persisted with the rest of the
 * cache, like `venues` — 18 rows on 30 Sept, hand-seeded, so no paging: the
 * table would need a thousand parties before PostgREST's cap mattered.
 *
 * The hour's `staleTime` is `useFeedDetail`'s reasoning: the table is edited
 * by a migration or by hand, never by a harvest, so it does not move between
 * two looks at the Club lane. A row still waiting on it draws no eyebrow
 * rather than a slug (`seriesName`).
 */
export const useSeries = () => useAuthed({
  queryKey: ['series'],
  queryFn: () => rows<SeriesRow>(supabase.from('series').select('id,name,kind,home_venue_id')),
  select: seriesById,
  staleTime: 60 * 60_000,
})

// ---------------------------------------------------------------------------
// Showings
// ---------------------------------------------------------------------------

/**
 * Every future showing, all disciplines.
 *
 * Feed and Venues each fetched their own variant of this — Feed with the
 * columns a sheet needs, Venues with the five that place a row in a building —
 * so the wider of the two is what everybody gets.
 *
 * One `showing_page` call, not `fetchAll` over `showing_list` (a4-12): the
 * view ran to 6,600 rows on 2 Oct, past PostgREST's silent 1,000-row cap, so
 * it paged — seven sequential round trips, 2.1 MB of objects repeating nine
 * key names. The function sends the same rows packed as `feed_page` packs the
 * feed, with each venue's name, district and building once rather than per
 * showing, and `decodeShowingPage` puts them back: the rows below are the
 * shape the view's select returned, every key present. A function's json
 * result is not subject to the row cap, so there is nothing to page.
 */
async function fetchShowings(): Promise<ShowingLite[]> {
  // A bound the caller fixes rather than the server's `now()`, as when this
  // paged: `showing_page` takes `max_rows`/`skip_rows` for a caller that ever
  // has to, and offsets over a moving bound skip the rows that just began.
  const from_at = new Date().toISOString()
  const { data, error } = await supabase.rpc('showing_page', { from_at })
  if (error) throw new Error(error.message)
  return decodeShowingPage<ShowingLite>(data as unknown as ShowingPagePayload)
}

const withIndex = (list: ShowingLite[]) => ({ rows: list, byEntry: indexShowings(list) })

export const useShowings = () =>
  useAuthed({ queryKey: ['showings'], queryFn: fetchShowings, select: withIndex })

// ---------------------------------------------------------------------------
// What you did with an entry
// ---------------------------------------------------------------------------

export interface EntryStateRow { entry_id: string; state: string; at: string }

/**
 * Paged rather than plainly selected.
 *
 * `entry_state` grows by a row for every save and every dismissal and never
 * shrinks on its own, so it is on the same road `feed` and `showing_list`
 * already went down: PostgREST answers an unbounded select with at most 1,000
 * rows and says nothing about it. A truncated read here would quietly stop
 * hiding the oldest dismissals. Ordered on `entry_id` because keyset paging
 * needs something unique and stable; the `at` ordering Saved wants is applied
 * below, once, per fetch.
 */
async function fetchEntryState(): Promise<EntryStateRow[]> {
  const { data, error } = await fetchAll<EntryStateRow>(() =>
    supabase.from('entry_state').select('entry_id,state,at').order('entry_id'))
  if (error) throw new Error(error.message)
  return data
}

const newestFirst = (list: EntryStateRow[]) =>
  [...list].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))

export const useEntryState = () =>
  useAuthed({ queryKey: ['entry_state'], queryFn: fetchEntryState, select: newestFirst })

/** The saved ids, built once per fetch however many rows ask: keyed on the
 *  array's identity, which the query keeps until `entry_state` changes. */
const savedSets = new WeakMap<EntryStateRow[], Set<string>>()
const savedSetOf = (list: EntryStateRow[]): Set<string> => {
  let set = savedSets.get(list)
  if (!set) { set = new Set(list.filter(r => r.state === 'saved').map(r => r.entry_id)); savedSets.set(list, set) }
  return set
}

/**
 * Is this one entry saved — `useEntryState()`, narrowed to a boolean.
 *
 * Every `EventRow` asks, and reading the whole list made every mounted row in
 * all five tabs re-render on any save, dismissal or forget anywhere (a4-13).
 * A `select` that answers for this id alone means TanStack re-renders the row
 * only when its own answer changes. Same key, same fetch, same cache.
 */
export function useIsSaved(id: string): boolean {
  const select = useCallback((list: EntryStateRow[]) => savedSetOf(list).has(id), [id])
  return useAuthed({ queryKey: ['entry_state'], queryFn: fetchEntryState, select }).data ?? false
}

/**
 * The saved backlog, as a number — X4, and it replaces the query `App.tsx`
 * ran on every tab change.
 *
 * Two decisions come with it from that query's own comment and are the reason
 * a plain `count(entry_state where state = 'saved')` will not do:
 *
 * - The badge follows the backlog, which now lives on Lists. A feed you have
 *   not read is not a debt; a thing you saved and never scheduled is.
 * - A save outlives the run it was made on. The show ends, or is cancelled,
 *   and the `entry_state` row stays `saved` while the entry drops out of
 *   `feed`. Saved itself already excludes those, so counting every `saved` row
 *   said a number the list this badge points at could not show (UI-audit F06).
 *
 * Derived, with no key of its own: both halves are already in the store, so
 * this costs no request at all — which is the whole of X4.
 */
export function useSavedCount(): number {
  const { data: feed } = useFeed()
  const { data: state } = useEntryState()
  return useMemo(() => {
    if (!feed || !state) return 0
    const live = new Set(feed.map(r => r.id))
    return state.filter(r => r.state === 'saved' && live.has(r.entry_id)).length
  }, [feed, state])
}

// ---------------------------------------------------------------------------
// Places
// ---------------------------------------------------------------------------

// One literal, not built with `+`: postgrest-js infers the returned row shape
// from this string at the type level, and concatenation widens it to plain
// `string` (the same note by `EVENT_FIELDS` in `venueRecord.ts`).
const VENUE_COLUMNS =
  'id,name,district,kind,commercial,parent_id,aliases,hero_key,hero_alt,hero_credit,hero_license,hero_source_url,street,postcode,country,lat,lng,site_url,closed_at'

/** Venues, with a building's inherited hero already resolved and an index by
 *  id. `aliases` rides along for Saved's lead matcher, which used to fetch
 *  `id,name,aliases` on its own. Paged: 733 rows on 2 Oct, growing ~110 a
 *  week, so the cap was a fortnight away. */
export const useVenues = () => useAuthed({
  queryKey: ['venues'],
  queryFn: () => allRows<VenueRow>(() => supabase.from('venues').select(VENUE_COLUMNS).order('id')),
  select: inheritVenueHeroes,
})

// ---------------------------------------------------------------------------
// WP 3.4b (P3): places Doug follows
// ---------------------------------------------------------------------------

/** A set rather than the rows themselves — every reader (the place page's
 *  button, Today's section) only ever asks "is this one in it". */
export const useFollows = () => useAuthed({
  queryKey: ['follows'],
  queryFn: async () => {
    const list = await rows<{ venue_id: string }>(supabase.from('venue_follows').select('venue_id'))
    return new Set(list.map(r => r.venue_id))
  },
})

// ---------------------------------------------------------------------------
// Lists
// ---------------------------------------------------------------------------

export const useLists = () => useAuthed({
  queryKey: ['lists'],
  queryFn: () => rows<List>(supabase.from('lists').select('*').order('position')),
})

/** `position`, then `created_at` and `id` (a2-14): an aisle's items often
 *  share a position (each add takes `items.length`), and Postgres returns
 *  ties in whatever order the heap has them — which an Undo, writing a row
 *  back as a fresh tuple, changed. The tiebreak makes the order a fact about
 *  the rows, so a restored row is back where it was. */
export const useListItems = () => useAuthed({
  queryKey: ['list_items'],
  queryFn: () => rows<ListItem>(
    supabase.from('list_items').select('*').order('position').order('created_at').order('id')),
})

/** Every run of every template/checklist, for the index's "last run" meta
 *  (`listSummary`). One query rather than one per list: the index draws every
 *  list's row at once, and `WP 4.5`'s own run-history view can filter this
 *  same cache by `list_id` instead of fetching it again. */
export const useListRuns = () => useAuthed({
  queryKey: ['list_runs'],
  queryFn: () => rows<ListRun>(supabase.from('list_runs').select('*').order('completed_at', { ascending: false })),
})

/** L3: the "usuals" shelf above a grocery list's aisles. `listUsuals` already
 *  orders by `times` descending; the tiebreak on `last_at` — the more
 *  recently added of two equally-frequent items leads — is applied here
 *  rather than in a second SQL `order by`, since a client-side stable sort on
 *  an already-ordered small array costs nothing extra. */
const usualsByFrequency = (list: ListUsual[]) =>
  [...list].sort((a, b) => (b.times !== a.times ? b.times - a.times : b.last_at.localeCompare(a.last_at)))

export const useListUsuals = (listId: string) => useAuthed({
  queryKey: ['list_usuals', listId],
  queryFn: () => rows<ListUsual>(listUsuals(listId)),
  // Module-level, not an inline arrow: a new function each render makes
  // TanStack re-run `select` and hand back a new array every time.
  select: usualsByFrequency,
})

/**
 * WP 4.4: open tasks with a `due_on`, for Today's Coming up and the Year's
 * timeline. Derived, like `useSavedCount` and `useAttentionCount` above —
 * every input is already in the store — but shaped as a query result rather
 * than a plain value, because both callers read `.data` the way every other
 * query on their screen already does (`Year.tsx`'s own `NONE_YET` fallback).
 */
export function useDatedTasks() {
  const listsQ = useLists()
  const itemsQ = useListItems()
  const data = useMemo(() => {
    if (!listsQ.data || !itemsQ.data) return undefined
    const taskListIds = new Set(listsQ.data.filter(l => l.kind === 'task').map(l => l.id))
    return itemsQ.data.filter(i => taskListIds.has(i.list_id) && !i.done_at && i.due_on)
  }, [listsQ.data, itemsQ.data])
  return { ...itemsQ, data }
}

/**
 * The Lists tab's badge (N2, B3): open tasks due today, plus saved events in
 * the next 48 hours. Derived, no query of its own — every input is already
 * in the store, the same trick `useSavedCount` uses.
 *
 * a2-20: the rule lives in `attention.ts` and answers with the things, not
 * the number. The badge is their count (`useAttentionCount`) and the Lists
 * page's "1 needs you: …" line is the things themselves (`useAttention`), so
 * the two read one computation and cannot disagree.
 */
export function useAttention(): AttentionItem[] {
  const { data: feed } = useFeed()
  const { data: state } = useEntryState()
  const { data: items } = useListItems()
  return useMemo(
    () => attentionItems(feed ?? [], state ?? [], items ?? [], new Date()),
    [feed, state, items],
  )
}

export const useAttentionCount = (): number => useAttention().length

// ---------------------------------------------------------------------------
// The year
// ---------------------------------------------------------------------------

/** U1.3 renamed `stays.kind` 'ireland' to 'home2'. A phone's persisted cache
 *  can still hold the old value until the refetch lands (and the refetch is
 *  one round trip behind the first paint), and a stay of a kind nothing draws
 *  is a trip that vanishes from the Year for that moment. Read through here,
 *  the old spelling is the new one. A module-level function, so `select`'s
 *  result is stable between renders. */
const legacyStayKinds = (data: Stay[]): Stay[] =>
  data.some(s => (s.kind as string) === 'ireland')
    ? data.map(s => ((s.kind as string) === 'ireland' ? { ...s, kind: 'home2' as const } : s))
    : data

export const useStays = () => useAuthed({
  queryKey: ['stays'],
  queryFn: () => rows<Stay>(supabase.from('stays').select('*').order('starts_on')),
  select: legacyStayKinds,
})

/** A screen's Retry after a failed read (a2-30): re-read every query on
 *  screen that ended in an error, and nothing that did not. Not each hook's
 *  own `refetch` — `useFeed` wraps its query and hands none back. */
export const refetchFailed = () => queryClient.refetchQueries({
  type: 'active',
  predicate: q => q.state.status === 'error',
})

export const useFlights = () => useAuthed({
  queryKey: ['flights'],
  queryFn: () => rows<Flight>(supabase.from('flights').select('*').order('on_date')),
})

export const useCommitments = () => useAuthed({
  queryKey: ['commitments'],
  queryFn: () => rows<Commitment>(supabase.from('commitments').select('*').order('on_date')),
})

export const useLeaveYears = () => useAuthed({
  queryKey: ['leave_years'],
  queryFn: () => rows<LeaveYear>(supabase.from('leave_years').select('*').order('year')),
})

export const useLeaveDays = () => useAuthed({
  queryKey: ['leave_days'],
  queryFn: () => rows<LeaveDay>(supabase.from('leave_days').select('day').order('day')),
})

/** WP 6.5: visitors, its own table rather than a `stays` row with
 *  `kind = 'visit'`. See `types.ts`'s `Visitor` for why. */
export const useVisitors = () => useAuthed({
  queryKey: ['visitors'],
  queryFn: () => rows<Visitor>(supabase.from('visitors').select('*').order('starts_on')),
})

// ---------------------------------------------------------------------------
// Home's citywide and health data
// ---------------------------------------------------------------------------

/** Fourteen days, which is DIRECTION's window. Anything older is not news.
 *  Keyed by region (C2.2): `['news', regionId]`, so the lane a switch lands
 *  on is its own cache, not Berlin's with a filter laid over it.
 *
 *  C4.1: `news.region_id` exists, every row written before it is Berlin's, and
 *  the Worker files each Boston source's rows under `boston`, so the lane is
 *  the table filtered to the region being looked at. (Until the column
 *  existed this read the table only for Berlin and answered an empty list for
 *  every other region.) A region with no sources has an empty lane, which
 *  `CityLines` and `City` already draw as nothing. */
export function useNews() {
  const { region } = useCurrentRegion()
  return useAuthed({
    queryKey: ['news', region.id],
    queryFn: () => rows<NewsRow>(supabase.from('news')
      .select('*')
      .eq('region_id', region.id)
      .gte('published_on', addDays(iso(new Date()), -14))
      .order('published_on', { ascending: false })),
  })
}

const WEATHER_URL = 'https://api.open-meteo.com/v1/forecast'

/** Open-Meteo's URL for a point and the zone its days are cut in. Two
 *  decimals is a kilometre, finer than the model's own grid. */
export function weatherUrl(lat: number, lng: number, timezone: string): string {
  return `${WEATHER_URL}?latitude=${lat.toFixed(2)}&longitude=${lng.toFixed(2)}`
    + `&daily=weathercode,temperature_2m_max&timezone=${encodeURIComponent(timezone)}&forecast_days=7`
}

/**
 * The day strip's forecast (WP 6.2). Not `useAuthed`: Open-Meteo takes no key
 * and needs no session, so the strip can draw it before sign-in resolves.
 *
 * `staleTime` an hour, because a forecast does not move under you the way the
 * feed does. A failed fetch is left as an error on this query and nowhere
 * else — `Today.tsx` reads `weatherQ.data ?? []` and never this query's
 * `error`, which is what makes a network failure a strip with no weather in
 * it rather than a screen that cannot show the day strip at all.
 */
export function useWeather() {
  // C2.2 (D5): where he is — the region's pin if he has one, else the
  // region's centre — and in the region's zone, which is what cuts the days.
  const { region } = useCurrentRegion()
  const pin = placePoint(useRegionPlace())
  const lat = pin?.lat ?? region.lat
  const lng = pin?.lng ?? region.lng
  return useQuery<DayForecast[]>({
    queryKey: ['weather', region.id],
    queryFn: async () => {
      const res = await fetch(weatherUrl(lat, lng, region.timezone))
      if (!res.ok) throw new Error(`weather: ${res.status}`)
      return parseForecast(await res.json())
    },
    staleTime: 60 * 60_000,
  })
}

export const useRecipes = () => useAuthed({
  queryKey: ['recipes'],
  queryFn: () => rows<Recipe>(supabase.from('recipes')
    .select('id,name,status,last_run_at,last_ok_at').order('name')),
})

export const useAlerts = () => useAuthed({
  queryKey: ['alerts'],
  queryFn: () => rows<Alert>(supabase.from('alerts')
    .select('*').is('acknowledged_at', null).order('created_at', { ascending: false })),
})

export interface HarvestRunRow {
  recipe_id: string | null
  model_cost_usd: number
  rows_valid: number | null
  status: string | null
  started_at: string
}

const weekAgo = () => new Date(Date.now() - 7 * 86400000).toISOString()

/** `days` defaults to 7 because the phone's account sheet reads this hook as
 *  "this week" (repair spend, and "no run in the last week" per recipe). The
 *  desk's Health asks for 14 and filters its week-bound cards by date. The
 *  7-day key is unchanged; a wider window gets its own.
 *
 *  Paged (a3-01). The harvest writes ~130 runs a day, so a week is ~1,100 rows
 *  and a fortnight ~1,850, both past PostgREST's silent 1,000-row cap: the
 *  newest thousand came back and the spend card summed those, $0.06 for a
 *  fortnight that cost $1.02. `id` breaks `started_at` ties so the pages are
 *  stable (`fetchAll`'s rule). */
function fetchHarvestRuns(days: number): Promise<HarvestRunRow[]> {
  const since = new Date(Date.now() - days * 86400000).toISOString()
  return allRows<HarvestRunRow>(() => supabase.from('harvest_runs')
    .select('recipe_id,model_cost_usd,rows_valid,status,started_at')
    .gte('started_at', since)
    .order('started_at', { ascending: false })
    .order('id'))
}

export const useHarvestRuns = (days = 7) => useAuthed({
  queryKey: days === 7 ? ['harvest_runs'] : ['harvest_runs', days],
  queryFn: () => fetchHarvestRuns(days),
})

/**
 * Every model cost the client can read, for Health's recorded total (a3-31):
 * page repairs (`harvest_runs.model_cost_usd`) and Ask turns
 * (`chat_messages.cost_usd`, the owner's own under RLS). Only rows that cost
 * something — a few dozen of each, not the thousands of clean runs — so the
 * whole history is two small reads. `healthWords.recordedSpend` sums them.
 */
export const useRecordedSpend = () => useAuthed({
  queryKey: ['recorded_spend'],
  queryFn: async () => {
    const [runs, turns] = await Promise.all([
      allRows<{ started_at: string; model_cost_usd: number | string }>(() => supabase.from('harvest_runs')
        .select('started_at,model_cost_usd').gt('model_cost_usd', 0).order('started_at').order('id')),
      allRows<{ created_at: string; cost_usd: number | string }>(() => supabase.from('chat_messages')
        .select('created_at,cost_usd').gt('cost_usd', 0).order('created_at').order('id')),
    ])
    return [
      ...runs.map(r => ({ at: r.started_at, usd: r.model_cost_usd })),
      ...turns.map(t => ({ at: t.created_at, usd: t.cost_usd })),
    ]
  },
})

/**
 * One "Something wrong with this?" note, with the recipe that produced the
 * listing it is about — embedded through `app_notes_entry_id_fkey`, so the join
 * happens once in Postgres rather than against whatever part of the feed the
 * client happens to be holding. (`entries` is readable: `entries_read` is
 * `using (true)` for `authenticated`, checked against live Postgres, so the
 * embed is never null for a row that still exists.)
 *
 * Both shapes, and only one of them can happen. Asked directly, PostgREST calls
 * a nullable forward FK `"cardinality": "many-to-one"` — measured against live
 * PostgREST on `entries.venue_id`, which is nullable the same way `entry_id` is
 * (`on delete set null`) — so the runtime value is an object, or null where the
 * entry has been re-harvested away. supabase-js's generated types widen it to
 * an array anyway, because their heuristic reads nullability as to-many.
 *
 * `recipeOf` reads either rather than asserting the generator away. The cost is
 * four lines; the cost of getting it the other way round is a counter that
 * silently reads zero, which is the exact failure this whole fix exists to
 * stop, and there are no rows carrying `entry_id` yet to catch it on.
 */
type Embedded<T> = T | T[] | null
export interface WrongReport { entries: Embedded<{ source_recipe: string | null }> }

const recipeOf = (r: WrongReport): string | null =>
  (Array.isArray(r.entries) ? r.entries[0] : r.entries)?.source_recipe ?? null

export { recipeOf }

/**
 * Listings reported wrong this week, by recipe — the health strip's last row.
 *
 * Was `useTasteSkips`, reading `taste_signal` for `action = 'skipped'`. WP 2.4
 * stopped writing that row: an adapter bug and a statement of taste were two
 * facts in one table, told apart by an action name, and the scorer read both.
 * The report moved to `app_notes`, where it is a sentence someone can act on,
 * so the counter follows it. The name follows too — nothing here is a taste
 * signal any more, and a hook called `useTasteSkips` reading the notes table
 * would be the comment that is worse than none.
 */
export const useWrongReports = () => useAuthed({
  queryKey: ['wrong_reports'],
  queryFn: () => rows<WrongReport>(supabase.from('app_notes')
    .select('entries(source_recipe)')
    .not('entry_id', 'is', null)
    .gte('created_at', weekAgo())),
})

// ---------------------------------------------------------------------------
// Leads, notes, releases
// ---------------------------------------------------------------------------

/** Paged: 403 leads in a fortnight, 256 of them last week. */
export const useLeads = () => useAuthed({
  queryKey: ['leads'],
  queryFn: () => allRows<Lead>(() => supabase.from('leads')
    .select('*').eq('status', 'open').order('created_at', { ascending: false }).order('id')),
})

/** U4.2: no user filter, on purpose. `app_notes` select is `user_id =
 *  auth.uid() or is_admin()`, so Postgres hands an admin everyone's rows (each
 *  carries `user_id`; `noteAuthor.ts` labels them) and anyone else only their
 *  own. A filter here would need `isAdmin` in the key, splitting the cache the
 *  optimistic patches in `mutations.ts` write by exact key. */
export const useNotes = () => useAuthed({
  queryKey: ['app_notes'],
  queryFn: () => rows<AppNote>(supabase.from('app_notes')
    .select('*').order('created_at', { ascending: false })),
})

/** Paged: it only grows (205 rows, ~50 a week). */
export const useReleaseNotes = () => useAuthed({
  queryKey: ['release_notes'],
  queryFn: () => allRows<ReleaseNote>(() => supabase.from('release_notes')
    .select('*')
    .order('shipped_on', { ascending: false })
    .order('created_at', { ascending: false })
    .order('id')),
})

// ---------------------------------------------------------------------------
// Venue admin
//
// Its own keys, and its own row shape: the admin sheet lists buildings only
// and reads twenty columns the five tab screens have no use for. Sharing
// `['venues']` would mean fetching all of it for everyone.
// ---------------------------------------------------------------------------

export interface AdminVenue {
  id: string
  name: string
  kind: string | null
  city: string | null
  commercial: boolean | null
  hero_key: string | null
  hero_alt: string | null
  hero_credit: string | null
  hero_license: string | null
  street: string | null
  postcode: string | null
  district: string | null
  country: string | null
  lat: number | null
  lng: number | null
  site_url: string | null
  specialism: string | null
  publishes: string | null
  source_notes: string | null
  organisation_id: string | null
  /** `venues.closed_at` — the editor's "Closed" switch writes it, the list
   *  shows it. Optional for the same reason `VenueRow`'s is. */
  closed_at?: string | null
  created_at: string
}

// One literal, not built with `+`: postgrest-js infers the returned row shape
// from this string at the type level, and concatenation widens it to plain
// `string`.
const ADMIN_VENUE_FIELDS =
  'id,name,kind,city,commercial,hero_key,hero_alt,hero_credit,hero_license,street,postcode,district,country,lat,lng,site_url,specialism,publishes,source_notes,organisation_id,closed_at,created_at'

export const useAdminVenues = () => useAuthed({
  queryKey: ['admin', 'venues'],
  queryFn: () => allRows<AdminVenue>(() => supabase.from('venues')
    .select(ADMIN_VENUE_FIELDS).is('parent_id', null).order('name').order('id')),
})

/** Fetched once for the whole list — `fetchUpcomingEvents` is deliberately one
 *  venue at a time, and the filter, the "most events" sort and the stats line
 *  all need every row's answer at once. */
export const useVenueEventCounts = () => useAuthed({
  queryKey: ['admin', 'venue_event_counts'],
  queryFn: fetchUpcomingEventCounts,
})

export const useVenueSourcing = (venueId: string) => useAuthed({
  queryKey: ['admin', 'venue_sourcing', venueId],
  queryFn: () => fetchVenueSourcing(venueId),
})

export const useOrganisation = (organisationId: string | null) => useAuthed({
  queryKey: ['admin', 'organisation', organisationId],
  queryFn: () => fetchOrganisation(organisationId),
})

export const useVenueEvents = (venueId: string) => useAuthed({
  queryKey: ['admin', 'venue_events', venueId],
  queryFn: () => fetchUpcomingEvents(venueId),
})

export const useVenueNotes = (venueId: string) => useAuthed({
  queryKey: ['admin', 'venue_notes', venueId],
  queryFn: () => fetchVenueNotes(venueId),
})
